import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, styled, Typography } from '@mui/material';
import PackageImage from '../assets/images/3d-icons/package.png';
import { BasketOrder, BasketOrdersDict } from '../hooks/useBasketOrders';
import MinimumOrderValueChip from '../common/components/MinimumOrderValueChip';
import { formatMinimumOrderValue } from '../common/utils/common.utils';

interface BasketMinimumOrderValuesProps {
  orders: BasketOrdersDict;
}

const MinimumOrderValueBox = styled(Box)(({ theme }) => ({
  borderRadius: (theme.shape.borderRadius as number) * 4,
  padding: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1),
  },
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.surfaceContainerLow.main,
  textAlign: 'center',
  fontSize: '0.875rem',
}));

const MinimumOrderValueContentBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.between('sm', 'md')]: {
    flexDirection: 'column',
  },
  justifyContent: 'center',
  alignItems: 'center',
}));

export default function BasketMinimumOrderValues({ orders }: BasketMinimumOrderValuesProps): React.JSX.Element {
  const { t } = useTranslation(['basket', 'common']);
  const [seeDetails, setSeeDetails] = useState(false);
  const unreachedMinimumOrderValueOrders = useMemo(
    () => Object.keys(orders).reduce((acc, tradeName) => {
      const order = orders[tradeName];

      if (order.minimumOrderValueProgression < order.minimumOrderValue) {
        acc.push(order);
      }

      return acc;
    }, [] as BasketOrder[]),
    [orders],
  );

  const toggleSeeDetails = (): void => {
    setSeeDetails(!seeDetails);
  };

  const seeDetailsButton = useMemo((): React.JSX.Element => (
    <Button
      variant="text"
      disableRipple
      onClick={toggleSeeDetails}
    >
      {t(`common:${seeDetails ? 'less_details' : 'see_details'}`)}
    </Button>
  ), [seeDetails]);

  const minimumOrderValueDetails = useMemo((): React.JSX.Element[] => {
    const minimumOrderValueBoxes = unreachedMinimumOrderValueOrders.map((order) => {
      const missingMinimumOrderValue = formatMinimumOrderValue(t, order.minimumOrderValue - order.minimumOrderValueProgression, order.minimumOrderValueType);

      return (
        <MinimumOrderValueBox key={order.name}>
          <MinimumOrderValueContentBox>
            <Box
              component="img"
              src={order.photoUrl}
              alt={t('common:company')}
              sx={{
                width: 24,
                height: 24,
                borderRadius: 2,
                mr: 1,
              }}
            />
            <Typography variant="bodyMedium">
              {order.name}
            </Typography>
          </MinimumOrderValueContentBox>
          <MinimumOrderValueContentBox>
            <Typography variant="bodySmall" sx={{ mr: 0.5 }}>
              {`${t('basket:minimum_order_value_missing', { value: missingMinimumOrderValue })}`} - {t('common:minimum_order_value')} :
            </Typography>
            <MinimumOrderValueChip
              minimumOrderValue={order.minimumOrderValue}
              minimumOrderValueType={order.minimumOrderValueType}
              minimumOrderValueProgression={order.minimumOrderValueProgression}
              enableProgress
            />
          </MinimumOrderValueContentBox>
        </MinimumOrderValueBox>
      );
    });

    return minimumOrderValueBoxes;
  }, [unreachedMinimumOrderValueOrders]);

  const basketMinimumOrderValues = useMemo((): React.JSX.Element => {
    if (!unreachedMinimumOrderValueOrders.length) {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>;
    }

    return (
      <Box sx={{ maxWidth: {xs: '100%', lg: 600} }}>
        {seeDetails
          ? (
            <Box sx={{ textAlign: 'center' }}>
              {seeDetailsButton}
              {minimumOrderValueDetails}
            </Box>
          )
          : (
            <MinimumOrderValueBox>
              <MinimumOrderValueContentBox>
                <img src={PackageImage} height={24} width={24} />
                <Typography variant="bodySmall" sx={{ ml: 0.5 }}>
                  {t('basket:unreached_minimum_order_value_for_N_suppliers', { count: unreachedMinimumOrderValueOrders.length })}
                </Typography>
              </MinimumOrderValueContentBox>
              {seeDetailsButton}
            </MinimumOrderValueBox>
          )}
      </Box>
    );
  }, [unreachedMinimumOrderValueOrders, seeDetails, minimumOrderValueDetails]);

  return basketMinimumOrderValues;
}
